import { createApiRef } from '@backstage/core-plugin-api';
import type {
  BudgetKeysResetResponse,
  BudgetResponse,
  BudgetWindowResponse,
  GatewayConfig,
  KeyActionResponse,
  KeyMetadata,
  ModelsResponse,
  SharedConfigResponse,
  UsageInsightsResponse,
  UsageResponse,
} from './types';

/**
 * {@link @backstage/core-plugin-api#ApiRef} for the agentgateway-keys API.
 *
 * @public
 */
export const agentgatewayKeysApiRef = createApiRef<AgentgatewayKeysApi>({
  id: 'plugin.agentgateway-keys.service',
});

/**
 * API client interface for the agentgateway-keys plugin.
 *
 * @public
 */
export interface AgentgatewayKeysApi {
  /** Returns the caller's key metadata, or `null` when no key exists. */
  getKey(): Promise<KeyMetadata | null>;
  /** Creates the caller's key. Throws if a key already exists. */
  createKey(): Promise<KeyActionResponse>;
  /** Rotates the caller's key. Throws if no key exists. */
  rotateKey(): Promise<KeyActionResponse>;
  /** Revokes (deletes) the caller's key. Throws if no key exists. */
  revokeKey(): Promise<void>;
  /**
   * Returns the caller's usage for the last `days` UTC calendar days, today
   * included.
   */
  getUsage(days: number): Promise<UsageResponse>;
  /**
   * Returns the figures behind the usage check-up (cache reuse, failed
   * requests, peaks) for the same period as {@link getUsage}.
   */
  getUsageInsights(days: number): Promise<UsageInsightsResponse>;
  /**
   * Returns the caller's spend limit (micro USD) for the configured budget
   * granularity, resolved solely from their tier via the configured
   * tier→budget map. No limit when the tier is missing/unknown or has no
   * configured budget. Best-effort `spentUsd`/`remainingUsd` for the window
   * so far are included when Prometheus has data.
   */
  getBudget(): Promise<BudgetResponse>;
  /**
   * Returns how the caller's spend built up over the current budget window
   * (today, or this month), plus the recent spend rate.
   */
  getBudgetWindow(): Promise<BudgetWindowResponse>;
  /**
   * Resets the caller's spent-budget counter by deleting their usd-budgets
   * rate-limit cache entries (all time windows). The tier-based limit itself
   * is unaffected. Returns what was deleted.
   */
  resetBudgetCache(): Promise<BudgetKeysResetResponse>;
  /** Returns the agentgateway public base URL for snippet generation. */
  getConfig(): Promise<GatewayConfig>;
  /** Returns the models deployed on the gateway, with llm-stats.com scores. */
  getModels(): Promise<ModelsResponse>;
  /**
   * Returns the raw Claude Code `settings.json` fetched from the shared-config
   * Git repo, with a timestamp of when it was last synced. Throws when the
   * shared config is not configured (503) or the fetch fails (502).
   */
  getSharedConfig(): Promise<SharedConfigResponse>;
  /** Same as `getSharedConfig`, but for the VS Code `settings.json`. */
  getVscodeSharedConfig(): Promise<SharedConfigResponse>;
}
